import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';

import { LandlordScoringShape } from '../../constants/shapes';

import { DSIcon } from '../../../base';

import { yearsSinceDate } from '../utils/dateUtils';

import PrivateLandlord from '../icons/PrivateLandlord';
import ProfessionalLandlord from '../icons/ProfessionalLandlord';

import Scoring from '../Scoring';
import Tag from '../Tag';

import {
  PRIVATE_LANDLORD,
  PROFESSIONAL_LANDLORD
} from '../ExtendedLandlordInformationContent/constants';

import styles from './ReducedLandlordInformation.module.scss';

const MAIN_LANGUAGES = [
  'spanish',
  'english',
  'portuguese',
  'french',
  'german',
  'italian'
];

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const LANDLORD_ICONS = {
  [PRIVATE_LANDLORD]: () => <PrivateLandlord color="blue" />,
  [PROFESSIONAL_LANDLORD]: () => <ProfessionalLandlord color="blue" />
};

const ReducedLandlordInformation = ({
  type,
  joiningDate = null,
  listingsPublished = null,
  averageResponseTime = null,
  languages = EMPTY_ARRAY,
  landlordScoring = EMPTY_OBJECT
}) => {
  const timeWithSpotahome = joiningDate ? yearsSinceDate(joiningDate) : null;
  const hasScoring =
    landlordScoring?.score != null &&
    landlordScoring?.totalBookingsClosed != null;

  const filteredCommonLanguages = languages.filter(language =>
    MAIN_LANGUAGES.includes(language)
  );
  const spokenLanguagesTranslated = filteredCommonLanguages
    .map(language => trans(`landlord_language_${language}`))
    .join(', ');

  const tagItems = [
    {
      show: averageResponseTime != null,
      icon: 'quick_phrases',
      label: trans('landlord.info.responds.within', {
        time: averageResponseTime
      })
    },
    {
      show: timeWithSpotahome != null,
      icon: 'furnished',
      label: trans('landlord.info.years.with.spotahome', {
        years: timeWithSpotahome
      })
    },
    {
      show: listingsPublished != null,
      icon: 'add_ad',
      label: `${trans(
        'landlord.info.listings.on.spotahome'
      )}: ${listingsPublished}`
    },
    {
      show: true,
      icon: 'emoji_language',
      label: trans('landlord.info.speaks', {
        languages:
          spokenLanguagesTranslated || trans('landlord_language_english')
      })
    }
  ];

  const tagsToShow = tagItems.filter(tag => tag.show);

  return (
    <div className={styles['reduced-landlord-information']}>
      <div className={styles['reduced-landlord-information__header']}>
        <div className={styles['reduced-landlord-information__icon-wrapper']}>
          {LANDLORD_ICONS[type]?.()}
        </div>
        <div className={styles['reduced-landlord-information__title-block']}>
          <h3 className={styles['reduced-landlord-information__type']}>
            {trans(`listing.landlord_info.type.${type}`)}
          </h3>
          <div className={styles['reduced-landlord-information__meta']}>
            <span className={styles['reduced-landlord-information__verified']}>
              <DSIcon
                className={
                  styles['reduced-landlord-information__verified-icon']
                }
                name="verified_filled"
                size="sm"
              />
              {trans('listing.landlord_info.verified')}
            </span>
            {hasScoring && (
              <Scoring
                score={landlordScoring.score}
                totalReviews={landlordScoring.totalBookingsClosed}
                size="medium"
                bold={false}
                tooltipPosition="top"
              />
            )}
          </div>
        </div>
      </div>

      <div className={styles['reduced-landlord-information__chips']}>
        {tagsToShow.map(tag => (
          <Tag
            key={tag.icon}
            label={tag.label}
            renderLeadingIcon={() => <DSIcon name={tag.icon} size="sm" />}
            isSpaced
            size="big"
            colorSchema="light"
          />
        ))}
      </div>
    </div>
  );
};

ReducedLandlordInformation.propTypes = {
  type: PropTypes.oneOf([PRIVATE_LANDLORD, PROFESSIONAL_LANDLORD]).isRequired,
  joiningDate: PropTypes.string,
  listingsPublished: PropTypes.number,
  averageResponseTime: PropTypes.string,
  languages: PropTypes.arrayOf(PropTypes.string),
  landlordScoring: PropTypes.shape(LandlordScoringShape)
};

export default ReducedLandlordInformation;
